import React, { useState, useEffect } from 'react';
import { Grid, Card, CardContent, Typography, Box, useTheme, useMediaQuery, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, TextField, FormControl, InputLabel, Select, MenuItem, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { useUserContext } from '../../../../context/user_context/UserContext';
import { organizationInvoiceSnapshotListener } from '../../../../repository/InvoicesDAO';
import AppSidebar from '../../../../components/navbar/DesktopAppSidebar';
import { Loading } from '../../../../components/loading/Loading';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { useOrganizationContext } from '../../../../context/organization_context/OrganizationContext';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);

function Dashboard() {
    const { t } = useTranslation();
    const { firebaseUser } = useUserContext();
    const [invoices, setInvoices] = useState();
    const [filteredInvoices, setFilteredInvoices] = useState();
    
    // Initialize date range to current month
    const getCurrentMonthDateRange = () => {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        
        // Format dates as YYYY-MM-DD for input fields
        const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        
        return {
            startDate: formatDate(firstDay),
            endDate: formatDate(lastDay)
        };
    };
    
    const [dateRange, setDateRange] = useState(getCurrentMonthDateRange());
    const [customerFilter, setCustomerFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const theme = useTheme();
    const { getCustomerByID, organizationCustomers } = useOrganizationContext();

    useEffect(() => {
        if (firebaseUser) {
            const unsubscribe = organizationInvoiceSnapshotListener(firebaseUser.organization, setInvoices);
            return () => unsubscribe();
        }
    }, [firebaseUser]);

    useEffect(() => {
        if (!invoices) return;

        let filtered = [...invoices];

        // Date range filter
        if (dateRange.startDate && dateRange.endDate) {
            const start = new Date(dateRange.startDate);
            const end = new Date(dateRange.endDate);
            filtered = filtered.filter(inv => {
                const date = new Date(inv.created_at.seconds * 1000);
                return date >= start && date <= end;
            });
        }

        // Customer filter
        if (customerFilter !== 'all') {
            filtered = filtered.filter(inv => inv.customer_id === customerFilter);
        }

        // Status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(inv => inv.status === statusFilter);
        }

        setFilteredInvoices(filtered);
    }, [invoices, dateRange, customerFilter, statusFilter]);

    const calculateMetrics = () => {
        if (!filteredInvoices) return null;

        const now = new Date();
        const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));

        // Basic metrics with MoM trends
        const thisMonthRevenue = filteredInvoices
            .filter(inv => inv.status === 'paid' && new Date(inv.created_at.seconds * 1000) >= thisMonth)
            .reduce((sum, inv) => {
                // Add the invoice total
                let amount = inv.total || 0;
                
                // Subtract refunded amount if the invoice has been refunded
                if (inv.refunded && inv.amount_refunded) {
                    amount -= inv.amount_refunded;
                }
                
                return sum + amount;
            }, 0) / 100;

        const lastMonthRevenue = filteredInvoices
            .filter(inv => {
                const date = new Date(inv.created_at.seconds * 1000);
                return inv.status === 'paid' && date >= lastMonth && date < thisMonth;
            })
            .reduce((sum, inv) => {
                // Add the invoice total
                let amount = inv.total || 0;
                
                // Subtract refunded amount if the invoice has been refunded
                if (inv.refunded && inv.amount_refunded) {
                    amount -= inv.amount_refunded;
                }
                
                return sum + amount;
            }, 0) / 100;

        const revenueMoMGrowth = lastMonthRevenue ? ((thisMonthRevenue - lastMonthRevenue) / lastMonthRevenue * 100) : 0;

        // Top customers with outstanding amounts
        const customerOutstanding = {};
        filteredInvoices
            .filter(inv => inv.status === 'open')
            .forEach(inv => {
                if (!customerOutstanding[inv.customer_id]) {
                    customerOutstanding[inv.customer_id] = 0;
                }
                customerOutstanding[inv.customer_id] += inv.total / 100;
            });

        const topOutstandingCustomers = Object.entries(customerOutstanding)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 5);

        // Payment method breakdown
        const paymentMethods = filteredInvoices
            .filter(inv => inv.status === 'paid' && inv.payment_method)
            .reduce((acc, inv) => {
                const method = inv.payment_method;
                if (!acc[method]) acc[method] = 0;
                acc[method]++;
                return acc;
            }, {});

        // Calculate failed payments
        const failedPayments = filteredInvoices
            .filter(inv => inv.payment_attempts && inv.payment_attempts.some(attempt => attempt.status === 'failed'))
            .length;

        // Basic metrics
        const totalInvoices = filteredInvoices.length;
        const paidInvoices = filteredInvoices.filter(inv => inv.status === 'paid').length;
        const actualRevenue = filteredInvoices
            .filter(inv => inv.status === 'paid')
            .reduce((sum, inv) => {
                // Add the invoice total
                let amount = inv.total || 0;
                
                // Subtract refunded amount if the invoice has been refunded
                if (inv.refunded && inv.amount_refunded) {
                    amount -= inv.amount_refunded;
                }
                
                return sum + amount;
            }, 0) / 100;
        const potentialRevenue = filteredInvoices
            .filter(inv => inv.status !== 'void')
            .reduce((sum, inv) => sum + (inv.total || 0), 0) / 100;
        const outstandingAmount = filteredInvoices
            .filter(inv => inv.status === 'open')
            .reduce((sum, inv) => sum + (inv.total || 0), 0) / 100;

        // Advanced metrics
        const averageInvoiceAmount = actualRevenue / paidInvoices || 0;
        const paymentRate = (paidInvoices / totalInvoices * 100) || 0;

        // Days to Pay Analysis
        const daysToPaySum = filteredInvoices
            .filter(inv => inv.status === 'paid' && inv.paid_at && inv.created_at)
            .reduce((sum, inv) => {
                const paidDate = new Date(inv.paid_at.seconds * 1000);
                const createdDate = new Date(inv.created_at.seconds * 1000);
                return sum + ((paidDate - createdDate) / (1000 * 60 * 60 * 24));
            }, 0);
        const averageDaysToPay = daysToPaySum / paidInvoices || 0;

        // Aging Analysis
        const aging = {
            current: 0,
            thirtyDays: 0,
            sixtyDays: 0,
            ninetyDays: 0,
            ninetyPlusDays: 0
        };

        filteredInvoices
            .filter(inv => inv.status === 'open')
            .forEach(inv => {
                const dueDate = new Date(inv.due_date.seconds * 1000);
                const daysPastDue = Math.floor((now - dueDate) / (1000 * 60 * 60 * 24));
                
                if (daysPastDue <= 0) aging.current += inv.total / 100;
                else if (daysPastDue <= 30) aging.thirtyDays += inv.total / 100;
                else if (daysPastDue <= 60) aging.sixtyDays += inv.total / 100;
                else if (daysPastDue <= 90) aging.ninetyDays += inv.total / 100;
                else aging.ninetyPlusDays += inv.total / 100;
            });

        // Customer Analysis
        const customerMetrics = {};
        filteredInvoices.forEach(inv => {
            if (!customerMetrics[inv.customer_id]) {
                customerMetrics[inv.customer_id] = {
                    totalAmount: 0,
                    invoiceCount: 0,
                    latePayments: 0
                };
            }
            
            // Only add to totalAmount if the invoice is paid
            if (inv.status === 'paid') {
                let amount = inv.total || 0;
                
                // Subtract refunded amount if the invoice has been refunded
                if (inv.refunded && inv.amount_refunded) {
                    amount -= inv.amount_refunded;
                }
                
                customerMetrics[inv.customer_id].totalAmount += amount / 100;
            }
            
            customerMetrics[inv.customer_id].invoiceCount++;
            if (inv.status === 'paid' && inv.paid_at && inv.due_date) {
                const paidDate = new Date(inv.paid_at.seconds * 1000);
                const dueDate = new Date(inv.due_date.seconds * 1000);
                if (paidDate > dueDate) {
                    customerMetrics[inv.customer_id].latePayments++;
                }
            }
        });

        // Top 5 customers by revenue
        const topCustomers = Object.entries(customerMetrics)
            .sort(([, a], [, b]) => b.totalAmount - a.totalAmount)
            .slice(0, 5);

        // Year over Year Growth
        const thisYear = now.getFullYear();
        const thisYearRevenue = filteredInvoices
            .filter(inv => 
                inv.status === 'paid' && 
                new Date(inv.created_at.seconds * 1000).getFullYear() === thisYear
            )
            .reduce((sum, inv) => sum + (inv.total || 0), 0) / 100;
        const lastYearRevenue = filteredInvoices
            .filter(inv => 
                inv.status === 'paid' && 
                new Date(inv.created_at.seconds * 1000).getFullYear() === thisYear - 1
            )
            .reduce((sum, inv) => sum + (inv.total || 0), 0) / 100;
        const yoyGrowth = lastYearRevenue ? ((thisYearRevenue - lastYearRevenue) / lastYearRevenue * 100) : 0;

        // Calculate total refunded amount
        const totalRefundedAmount = filteredInvoices
            .filter(inv => inv.refunded && inv.amount_refunded)
            .reduce((sum, inv) => sum + (inv.amount_refunded || 0), 0) / 100;

        return {
            totalInvoices,
            paidInvoices,
            actualRevenue,
            potentialRevenue,
            outstandingAmount,
            averageInvoiceAmount,
            paymentRate,
            averageDaysToPay,
            aging,
            topCustomers,
            yoyGrowth,
            customerMetrics,
            thisMonthRevenue,
            lastMonthRevenue,
            revenueMoMGrowth,
            topOutstandingCustomers,
            paymentMethods,
            failedPayments,
            totalRefundedAmount
        };
    };

    const prepareChartData = () => {
        if (!filteredInvoices) return null;

        // Monthly revenue data
        const monthlyData = {};
        const monthlyRefunds = {};
        const statusData = { paid: 0, open: 0, draft: 0, void: 0, refunded: 0 };
        const last6Months = new Array(6).fill(0).map((_, i) => {
            const d = new Date();
            d.setMonth(d.getMonth() - i);
            return d.toLocaleString('default', { month: 'short' });
        }).reverse();

        // Payment timeline data
        const paymentTimeline = {
            onTime: 0,
            late: 0,
            pending: 0
        };

        // Daily/Weekly patterns
        const dayOfWeekData = {
            Sunday: 0, Monday: 0, Tuesday: 0,
            Wednesday: 0, Thursday: 0, Friday: 0, Saturday: 0
        };

        filteredInvoices.forEach(invoice => {
            // Status count - count refunded invoices separately
            if (invoice.refunded) {
                statusData.refunded = (statusData.refunded || 0) + 1;
            } else {
                statusData[invoice.status] = (statusData[invoice.status] || 0) + 1;
            }

            // Monthly revenue - only count paid invoices
            if (invoice.status === 'paid') {
                const date = new Date(invoice.created_at.seconds * 1000);
                const monthYear = date.toLocaleString('default', { month: 'short' });
                if (last6Months.includes(monthYear)) {
                    monthlyData[monthYear] = (monthlyData[monthYear] || 0) + (invoice.total || 0) / 100;
                }
            }
            
            // Track refunds by month
            if (invoice.refunded && invoice.amount_refunded) {
                const date = new Date(invoice.created_at.seconds * 1000);
                const monthYear = date.toLocaleString('default', { month: 'short' });
                if (last6Months.includes(monthYear)) {
                    monthlyRefunds[monthYear] = (monthlyRefunds[monthYear] || 0) + (invoice.amount_refunded || 0) / 100;
                }
            }

            // Payment timeline analysis
            if (invoice.status === 'paid' && invoice.paid_at && invoice.due_date) {
                const paidDate = new Date(invoice.paid_at.seconds * 1000);
                const dueDate = new Date(invoice.due_date.seconds * 1000);
                if (paidDate <= dueDate) {
                    paymentTimeline.onTime++;
                } else {
                    paymentTimeline.late++;
                }
            } else if (invoice.status === 'open') {
                paymentTimeline.pending++;
            }

            // Day of week analysis
            const date = new Date(invoice.created_at.seconds * 1000);
            const dayOfWeek = date.toLocaleString('en-US', { weekday: 'long' });
            dayOfWeekData[dayOfWeek]++;
        });
        
        // Adjust monthly revenue by subtracting refunds
        last6Months.forEach(month => {
            if (monthlyRefunds[month]) {
                monthlyData[month] = (monthlyData[month] || 0) - monthlyRefunds[month];
            }
        });

        return {
            monthlyRevenue: {
                labels: last6Months,
                datasets: [{
                    data: last6Months.map(month => monthlyData[month] || 0),
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.main,
                    tension: 0.1
                }]
            },
            invoiceStatus: {
                labels: Object.keys(statusData).map(status => t(status.toUpperCase())),
                datasets: [{
                    data: Object.values(statusData),
                    backgroundColor: [
                        theme.palette.success.main,
                        theme.palette.warning.main,
                        theme.palette.info.main,
                        theme.palette.error.main,
                        theme.palette.error.light // For refunded
                    ]
                }]
            },
            paymentTimeline: {
                labels: [t(StringKeys.PAID_ON_TIME), t(StringKeys.PAID_LATE), t(StringKeys.PENDING_PAYMENT)],
                datasets: [{
                    data: [
                        paymentTimeline.onTime,
                        paymentTimeline.late,
                        paymentTimeline.pending
                    ],
                    backgroundColor: [
                        theme.palette.success.main,
                        theme.palette.error.main,
                        theme.palette.warning.main
                    ],
                    borderWidth: 0,
                    borderRadius: 4
                }]
            },
            dayOfWeek: {
                labels: Object.keys(dayOfWeekData),
                datasets: [{
                    data: Object.values(dayOfWeekData),
                    backgroundColor: theme.palette.primary.main,
                    borderWidth: 0,
                    borderRadius: 4,
                    hoverBackgroundColor: theme.palette.primary.dark
                }]
            }
        };
    };

    if (!filteredInvoices) return <AppSidebar content={<Loading />} />;

    const metrics = calculateMetrics();
    const chartData = prepareChartData();

    // Empty state component
    const EmptyState = ({ message }) => (
        <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            color: 'text.secondary'
        }}>
            <Typography variant="body1">{message}</Typography>
        </Box>
    );

    // Render metric with trend
    const MetricCard = ({ title, value, trend, prefix = '$', format = 'number' }) => {
        // Handle undefined, null, or NaN values
        const isValidNumber = value !== undefined && value !== null && !isNaN(value);
        
        if (!isValidNumber || (value === 0 && trend === 0)) {
            return (
                <Card>
                    <CardContent>
                        <Typography color="textSecondary" gutterBottom>{title}</Typography>
                        <EmptyState message={t(StringKeys.NO_DATA_AVAILABLE)} />
                    </CardContent>
                </Card>
            );
        }

        return (
            <Card>
                <CardContent>
                    <Typography color="textSecondary" gutterBottom>{title}</Typography>
                    <Typography variant="h4">
                        {format === 'currency' ? `${prefix}${value.toFixed(2)}` : value.toFixed(1)}
                        {format === 'percentage' ? '%' : ''}
                    </Typography>
                    {trend !== undefined && !isNaN(trend) && (
                        <Typography variant="body2" color={trend >= 0 ? "success.main" : "error.main"}>
                            {trend > 0 ? '+' : ''}{trend.toFixed(1)}% MoM
                        </Typography>
                    )}
                </CardContent>
            </Card>
        );
    };

    return (
        <AppSidebar
            content={
                <Box sx={{ p: 3 }}>
                    <Typography variant="h4" gutterBottom>
                        {t(StringKeys.INVOICE_DASHBOARD)}
                    </Typography>

                    {/* Filters */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        label={t(StringKeys.START_DATE)}
                                        type="date"
                                        value={dateRange.startDate}
                                        onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        label={t(StringKeys.END_DATE)}
                                        type="date"
                                        value={dateRange.endDate}
                                        onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth>
                                        <InputLabel>{t(StringKeys.CUSTOMER)}</InputLabel>
                                        <Select
                                            value={customerFilter}
                                            onChange={(e) => setCustomerFilter(e.target.value)}
                                            label={t(StringKeys.CUSTOMER)}
                                        >
                                            <MenuItem value="all">{t(StringKeys.ALL)}</MenuItem>
                                            {organizationCustomers && organizationCustomers.length > 0 ? (
                                                organizationCustomers.map(customer => (
                                                    <MenuItem key={customer.id} value={customer.id}>
                                                        {customer.first_name} {customer.last_name}
                                                    </MenuItem>
                                                ))
                                            ) : (
                                                <MenuItem disabled>
                                                    {t(StringKeys.NO_CUSTOMERS_AVAILABLE)}
                                                </MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth>
                                        <InputLabel>{t(StringKeys.STATUS)}</InputLabel>
                                        <Select
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                            label={t(StringKeys.STATUS)}
                                        >
                                            <MenuItem value="all">{t(StringKeys.ALL)}</MenuItem>
                                            <MenuItem value="draft">{t(StringKeys.DRAFT)}</MenuItem>
                                            <MenuItem value="open">{t(StringKeys.OPEN)}</MenuItem>
                                            <MenuItem value="paid">{t(StringKeys.PAID)}</MenuItem>
                                            <MenuItem value="void">{t(StringKeys.VOID)}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Key Financial Metrics */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={8}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {t(StringKeys.REVENUE_OVERVIEW)}
                                    </Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} sm={6}>
                                            <Stack spacing={2}>
                                                <Box>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        {t(StringKeys.ACTUAL_REVENUE)}
                                                    </Typography>
                                                    <Typography variant="h4">
                                                        ${metrics.actualRevenue.toFixed(2)}
                                                    </Typography>
                                                    <Typography variant="body2" color={metrics.revenueMoMGrowth >= 0 ? "success.main" : "error.main"}>
                                                        {metrics.revenueMoMGrowth > 0 ? '+' : ''}{metrics.revenueMoMGrowth.toFixed(1)}% MoM
                                                    </Typography>
                                                    {metrics.totalRefundedAmount > 0 && (
                                                        <Typography variant="body2" color="error.main">
                                                            ${metrics.totalRefundedAmount.toFixed(2)} {t(StringKeys.REFUNDED)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Box>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        {t(StringKeys.POTENTIAL_REVENUE)}
                                                    </Typography>
                                                    <Typography variant="h4">
                                                        ${metrics.potentialRevenue.toFixed(2)}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Stack spacing={2}>
                                                <Box>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        {t(StringKeys.OUTSTANDING_AMOUNT)}
                                                    </Typography>
                                                    <Typography variant="h4">
                                                        ${metrics.outstandingAmount.toFixed(2)}
                                                    </Typography>
                                                    <Typography variant="body2" color="warning.main">
                                                        {metrics.potentialRevenue > 0 
                                                            ? ((metrics.outstandingAmount / metrics.potentialRevenue) * 100).toFixed(1) 
                                                            : '0.0'}% of potential
                                                    </Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        {t(StringKeys.PAYMENT_RATE)}
                                                    </Typography>
                                                    <Typography variant="h4">
                                                        {metrics.totalInvoices > 0 
                                                            ? metrics.paymentRate.toFixed(1) 
                                                            : '0.0'}%
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {t(StringKeys.PAYMENT_PERFORMANCE)}
                                    </Typography>
                                    <Stack spacing={3}>
                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                {t(StringKeys.AVERAGE_DAYS_TO_PAY)}
                                            </Typography>
                                            <Typography variant="h4">
                                                {!isNaN(metrics.averageDaysToPay) && metrics.paidInvoices > 0 
                                                    ? metrics.averageDaysToPay.toFixed(1) 
                                                    : '0.0'} {t(StringKeys.DAYS)}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                {t(StringKeys.OVERDUE_INVOICES)}
                                            </Typography>
                                            <Typography variant="h4" color="error.main">
                                                {metrics.aging.ninetyPlusDays > 0 ? metrics.aging.ninetyPlusDays.toFixed(2) : 0}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                90+ {t(StringKeys.DAYS)} {t(StringKeys.OVERDUE)}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Aging Analysis */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {t(StringKeys.AGING_ANALYSIS)}
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6} md={2.4}>
                                            <Typography variant="body2" color="textSecondary">
                                                {t(StringKeys.CURRENT)}
                                            </Typography>
                                            <Typography variant="h6">
                                                ${metrics.aging.current.toFixed(2)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2.4}>
                                            <Typography variant="body2" color="textSecondary">
                                                1-30 {t(StringKeys.DAYS)}
                                            </Typography>
                                            <Typography variant="h6">
                                                ${metrics.aging.thirtyDays.toFixed(2)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2.4}>
                                            <Typography variant="body2" color="textSecondary">
                                                31-60 {t(StringKeys.DAYS)}
                                            </Typography>
                                            <Typography variant="h6">
                                                ${metrics.aging.sixtyDays.toFixed(2)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2.4}>
                                            <Typography variant="body2" color="textSecondary">
                                                61-90 {t(StringKeys.DAYS)}
                                            </Typography>
                                            <Typography variant="h6">
                                                ${metrics.aging.ninetyDays.toFixed(2)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2.4}>
                                            <Typography variant="body2" color="textSecondary">
                                                90+ {t(StringKeys.DAYS)}
                                            </Typography>
                                            <Typography variant="h6" color="error.main">
                                                ${metrics.aging.ninetyPlusDays.toFixed(2)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Charts */}
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {t(StringKeys.MONTHLY_REVENUE)}
                                    </Typography>
                                    <Box sx={{ height: 300 }}>
                                        <Line
                                            data={chartData.monthlyRevenue}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                    legend: {
                                                        display: false
                                                    },
                                                    tooltip: {
                                                        callbacks: {
                                                            label: (context) => `$${context.parsed.y.toFixed(2)}`
                                                        }
                                                    }
                                                },
                                                scales: {
                                                    y: {
                                                        beginAtZero: true,
                                                        title: {
                                                            display: true,
                                                            text: t(StringKeys.REVENUE)
                                                        },
                                                        ticks: {
                                                            callback: (value) => '$' + value
                                                        }
                                                    }
                                                }
                                            }}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {t(StringKeys.INVOICE_STATUS_DISTRIBUTION)}
                                    </Typography>
                                    <Box sx={{ height: 300 }}>
                                        <Doughnut
                                            data={chartData.invoiceStatus}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                    tooltip: {
                                                        callbacks: {
                                                            label: (context) => {
                                                                const label = context.label || '';
                                                                const value = context.parsed || 0;
                                                                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                                                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                                                                return `${label}: ${value} (${percentage}%)`;
                                                            }
                                                        }
                                                    },
                                                    legend: {
                                                        position: 'right',
                                                        labels: {
                                                            boxWidth: 15,
                                                            padding: 15
                                                        }
                                                    }
                                                }
                                            }}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {t(StringKeys.PAYMENT_STATUS_DISTRIBUTION)}
                                    </Typography>
                                    <Box sx={{ height: 300 }}>
                                        <Bar
                                            data={chartData.paymentTimeline}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                    legend: {
                                                        display: false
                                                    },
                                                    tooltip: {
                                                        callbacks: {
                                                            title: (context) => context[0].label
                                                        }
                                                    }
                                                },
                                                scales: {
                                                    y: {
                                                        beginAtZero: true,
                                                        title: {
                                                            display: true,
                                                            text: t(StringKeys.INVOICE_COUNT)
                                                        },
                                                        ticks: {
                                                            precision: 0
                                                        }
                                                    }
                                                }
                                            }}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {t(StringKeys.INVOICES_ISSUED_BY_DAY)}
                                    </Typography>
                                    <Box sx={{ height: 300 }}>
                                        <Bar
                                            data={chartData.dayOfWeek}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                    legend: {
                                                        display: false
                                                    },
                                                    tooltip: {
                                                        callbacks: {
                                                            title: (context) => context[0].label
                                                        }
                                                    }
                                                },
                                                scales: {
                                                    y: {
                                                        beginAtZero: true,
                                                        title: {
                                                            display: true,
                                                            text: t(StringKeys.INVOICE_COUNT)
                                                        },
                                                        ticks: {
                                                            precision: 0
                                                        }
                                                    }
                                                }
                                            }}
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Top Customers */}
                    <Grid container spacing={3} sx={{ mt: 3 }}>
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {t(StringKeys.TOP_CUSTOMERS)}
                                    </Typography>
                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>{t(StringKeys.CUSTOMER)}</TableCell>
                                                    <TableCell align="right">{t(StringKeys.ACTUAL_REVENUE)}</TableCell>
                                                    <TableCell align="right">{t(StringKeys.INVOICE_COUNT)}</TableCell>
                                                    <TableCell align="right">{t(StringKeys.LATE_PAYMENT_RATE)}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {metrics.topCustomers.map(([customerId, data]) => (
                                                    <TableRow key={customerId}>
                                                        <TableCell>
                                                            {getCustomerByID(customerId).first_name} {getCustomerByID(customerId).last_name}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            ${data.totalAmount.toFixed(2)}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {data.invoiceCount}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {data.invoiceCount > 0 
                                                                ? ((data.latePayments / data.invoiceCount) * 100).toFixed(1) 
                                                                : '0.0'}%
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>


                    {/* Top Outstanding Invoices by Customer */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {t(StringKeys.TOP_OUTSTANDING_BY_CUSTOMER)}
                                    </Typography>
                                    {metrics.topOutstandingCustomers.length > 0 ? (
                                        <TableContainer>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>{t(StringKeys.CUSTOMER)}</TableCell>
                                                        <TableCell align="right">{t(StringKeys.OUTSTANDING_AMOUNT)}</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {metrics.topOutstandingCustomers.map(([customerId, amount]) => (
                                                        <TableRow key={customerId}>
                                                            <TableCell>{getCustomerByID(customerId).first_name} {getCustomerByID(customerId).last_name}</TableCell>
                                                            <TableCell align="right">${amount.toFixed(2)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    ) : (
                                        <EmptyState message={t(StringKeys.NO_OUTSTANDING_INVOICES)} />
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Failed Payments Alert */}
                    {metrics.failedPayments > 0 && (
                        <Grid container spacing={3} sx={{ mb: 3 }}>
                            <Grid item xs={12}>
                                <Card sx={{ bgcolor: 'error.light' }}>
                                    <CardContent>
                                        <Typography variant="h6" color="error.main">
                                            {t(StringKeys.FAILED_PAYMENTS_ALERT, { count: metrics.failedPayments })}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}
                </Box>
            }
        />
    );
}

export default Dashboard;